/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, Divider } from 'omakase';
import { Form, Formik } from 'formik';
import {
  FormDatePickerField,
  FormFormattedTextField,
  FormPhoneCountryCodeField,
  FormPhoneField,
  FormSelectField,
  FormTextField,
  Grid,
} from 'ui';
import { LocalDate, LocalTime } from 'js-joda';
import { businessDayTimeCutOff } from 'utils/constants';
import { core } from '@tock/coretypes';
import { integerOrEmpty } from '@tock/shared/filters/wholeNumbers';
import { isEmail, requireFields } from '@tock/shared/utils/FormValidators';
import { reduce } from 'es-toolkit/compat';
import FormTimeSelectField from 'ui/FormTimeSelectField';

interface Props {
  patronProfile?: core.IPatronProfile;
  onSubmit(formData: FormValues): Promise<void>;
}

export interface FormValues {
  firstName: string;
  lastName: string;
  date: string | undefined;
  email: string;
  phone: string;
  phoneCountryCode: string;
  guestCount: string;
  eventType: string;
  notes: string;
  startTime: string | undefined;
  endTime: string | undefined;
}

const eventNames = [
  'Anniversary',
  'Baby shower',
  'Birthday',
  'Bridal / wedding shower',
  'Corporate event',
  'Engagement',
  'Graduation',
  'Holiday event',
  'Rehearsal dinner',
  'Wedding reception',
  'Other',
];

export const eventTypes: SelectOption[] = reduce(
  eventNames,
  (acc: SelectOption[], value: string) => {
    return acc.concat([
      {
        key: value,
        label: value,
        value,
      },
    ]);
  },
  []
);

export const validate = ({
  firstName,
  lastName,
  email,
  phone,
  date,
  guestCount,
  eventType,
  notes,
  startTime,
  endTime,
}: FormValues) => {
  const requiredErrors = requireFields(
    {
      firstName,
      lastName,
      phone,
      date,
      guestCount,
      eventType,
      notes,
      startTime,
      endTime,
    },
    'firstName',
    'lastName',
    'phone',
    'date',
    'guestCount',
    'eventType',
    'notes',
    'startTime',
    'endTime'
  );

  // TODO when OldPrivateDinginRequestForm is removed, type this as FormikErrors
  const errors: Dictionary<string> = {
    ...requiredErrors,
  };

  const message = 'Please enter a valid email';
  if (!email || !isEmail(email)) {
    errors.email = message;
  }

  const timeMessage = 'Start time must be before end time';

  if (startTime && endTime) {
    const startTimeAdjusted = LocalTime.parse(startTime as any).minusHours(4);
    const endTimeAdjusted = LocalTime.parse(endTime as any).minusHours(4);

    if (startTimeAdjusted.isAfter(endTimeAdjusted)) {
      errors.startTime = timeMessage;
    }
  }

  if (notes && notes.length > 750) {
    errors.notes = 'Notes must be 750 characters max';
  }

  if (parseInt(guestCount) <= 0) {
    errors.guestCount = 'Number of guests must be greater than 0';
  }

  return errors;
};

const PrivateDiningRequestForm: React.FunctionComponent<Props> = ({ onSubmit, patronProfile }) => {
  const startOfDay = LocalTime.parse(businessDayTimeCutOff);
  const endOfDay = startOfDay.minusMinutes(15);

  return (
    <Formik<FormValues>
      initialValues={{
        firstName: patronProfile?.patron?.firstName ?? '',
        lastName: patronProfile?.patron?.lastName ?? '',
        email: patronProfile?.patron?.email ?? '',
        phone: patronProfile?.patron?.phone ?? '',
        phoneCountryCode: patronProfile?.patron?.phoneCountryCode ?? '',
        guestCount: '',
        eventType: '',
        notes: '',
        date: undefined,
        startTime: undefined,
        endTime: undefined,
      }}
      onSubmit={onSubmit}
      validate={validate}
    >
      <Form>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <FormTextField label="First name" name="firstName" fullWidth />
          </Grid>
          <Grid item xs={6}>
            <FormTextField label="Last name" name="lastName" fullWidth />
          </Grid>
          <Grid item xs={12}>
            <FormTextField label="Email" name="email" fullWidth />
          </Grid>
          <Grid item xs={6}>
            <FormPhoneCountryCodeField fullWidth />
          </Grid>
          <Grid item xs={6}>
            <FormPhoneField fullWidth />
          </Grid>
          <Grid item xs={12}>
            <Divider mt={1} mb={1} />
          </Grid>
          <Grid item xs={12}>
            <FormDatePickerField
              name="date"
              label="Select preferred date"
              fullWidth
              DatePickerProps={{
                latestSelectableDate: LocalDate.now().plusYears(2),
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <FormTimeSelectField
              name="startTime"
              label="Preferred start time"
              minLocalTimeInclusive={startOfDay}
              maxLocalTimeInclusive={endOfDay}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <FormTimeSelectField
              name="endTime"
              label="Preferred end time"
              minLocalTimeInclusive={startOfDay}
              maxLocalTimeInclusive={endOfDay}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <FormFormattedTextField
              name="guestCount"
              label="Number of guests"
              filter={integerOrEmpty}
              fullWidth
              helperText="Number of guests must be greater than 0"
            />
          </Grid>
          <Grid item xs={12}>
            <FormSelectField name="eventType" label="Type of event" fullWidth>
              <option key="" value=""></option>
              {eventTypes.map((selectOption) => (
                <option key={selectOption.key} value={selectOption.value}>
                  {selectOption.label}
                </option>
              ))}
            </FormSelectField>
          </Grid>
          <Grid item xs={12}>
            <FormTextField
              name="notes"
              label="Event notes"
              placeholder="Tell us more about your event including the times that you are interested in, your budget, and any specific needs of your party*"
              rowsMax={4}
              rows={4}
              multiline
              helperText="750 characters max"
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <Button type="submit" size="large" fullWidth variant="primary">
              Submit request
            </Button>
          </Grid>
        </Grid>
      </Form>
    </Formik>
  );
};

export default PrivateDiningRequestForm;
